import React from "react";
import { ArrowRight, GraduationCap, Users, Award } from "lucide-react";

const Stitch = ({ className = "", color = "#E0A526" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

/* Stats styled as garment hang-tags, threaded along one stitch line —
   a nod to the tailoring & craft training the Trust runs. */
const HangTag = ({ icon: Icon, value, label }) => (
  <div className="relative pl-6 shrink-0">
    <span className="absolute left-[3px] top-[18px] w-[7px] h-[7px] rounded-full bg-[#082440] ring-[3px] ring-[#E0A526]/40" />
    <div
      className="pl-4 pr-5 py-3 bg-white/[0.06] border border-white/15"
      style={{ clipPath: "polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)" }}
    >
      <Icon className="w-4 h-4 text-[#E0A526] mb-2" />
      <h3 className="mbf-display text-[26px] sm:text-[30px] font-semibold text-white leading-none tracking-tight">
        {value}
      </h3>
      <p className="text-[12px] text-blue-100/60 mt-1.5 mbf-body">{label}</p>
    </div>
  </div>
);

const Home = () => {
  return (
    <section
      id="home"
      className="relative overflow-hidden bg-[#082440] text-white pt-[76px] md:pt-[84px]"
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* subtle woven-grid texture instead of blurred blob decoration */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
        }}
      />
      <div className="absolute top-0 right-0 w-[26rem] h-[26rem] bg-[#E0A526]/10 rounded-full blur-3xl -mr-32 -mt-32" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-3 w-fit mbf-mono">
              <span className="text-[12px] tracking-[0.18em] uppercase text-[#E0A526]">
                Est. 18 September 2015
              </span>
              <Stitch className="w-14 h-2" color="#E0A526" />
              <span className="text-[12px] tracking-[0.18em] uppercase text-blue-100/50">
                Registered Public Trust
              </span>
            </div>

            <h1 className="mbf-display mt-7 text-4xl sm:text-5xl lg:text-[3.4rem] font-semibold leading-[1.1] tracking-tight">
              Skills in hand.
              <span className="block mt-1 text-transparent bg-clip-text bg-gradient-to-r from-[#E0A526] via-[#eec267] to-white">
                A livelihood for life.
              </span>
            </h1>

            <p className="mbf-body mt-6 text-[16px] text-blue-100/75 leading-relaxed max-w-xl">
              MBFSDL is a registered public charitable trust that trains youth,
              women, and rural communities in employable skills — turning
              vocational training into steady, dignified livelihoods across India.
            </p>

            <div className="mt-9 flex flex-wrap gap-4 mbf-body">
              <a
                href="#programs"
                className="bg-[#E0A526] hover:bg-[#eec267] text-[#082440] font-semibold px-7 py-3.5 rounded-sm transition-all duration-300 flex items-center gap-2 group"
              >
                Explore Programs
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>

              <a
                href="#contact"
                className="border border-white/25 hover:border-white/50 hover:bg-white/5 px-7 py-3.5 rounded-sm font-semibold transition-all duration-300"
              >
                Contact Us
              </a>
            </div>

            {/* Hang-tag stats, strung on a stitch line */}
            <div className="mt-16 pt-2">
              <Stitch className="w-full max-w-md h-2 mb-6" color="#2E7D32" />
              <div className="flex flex-wrap gap-x-6 gap-y-6">
                <HangTag icon={Users} value="5,000+" label="Students Empowered" />
                <HangTag icon={Award} value="13" label="Core Objectives" />
                <HangTag icon={GraduationCap} value="10+" label="Years of Service" />
              </div>
            </div>
          </div>

          {/* Right Image Container */}
          <div className="lg:col-span-5 relative mt-4 lg:mt-0">
            <div className="relative border border-[#E0A526]/40 p-3">
              <div className="overflow-hidden">
                <img
                  src="/images/hero.jpg"
                  alt="MBFSDL Skill Training"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              {/* stitched corner marks */}
              <span className="absolute -top-[3px] -left-[3px] w-4 h-4 border-t-2 border-l-2 border-[#E0A526]" />
              <span className="absolute -bottom-[3px] -right-[3px] w-4 h-4 border-b-2 border-r-2 border-[#E0A526]" />
            </div>

            {/* Tag badge, pinned like a garment label */}
            <div
              className="absolute -bottom-6 -left-6 bg-[#FBF9F3] text-[#0A355C] p-4 shadow-xl max-w-[230px] hidden sm:block"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 16px 100%, 0 82%)" }}
            >
              <h4 className="mbf-display font-semibold text-[14px]">
                Practical Education
              </h4>
              <p className="mbf-body text-[12px] text-[#6b6355] mt-1">
                Training built for self-reliance
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;